import { useState, useCallback, useEffect, useMemo } from 'react';

import ExpandLessRoundedIcon from '@material-ui/icons/ExpandLessRounded';
import RemoveRoundedIcon from '@material-ui/icons/RemoveRounded';
import { useMutation, useQuery } from '@tanstack/react-query';
import ClearIcon from '@material-ui/icons/Clear';
import { useSnackbar } from 'notistack';
import { TContact } from '@setvi/shared/components/sdrawer-content/components/contact';
import { DrawerContent, LinkObjectType } from '@setvi/shared/enums';
import {
  CompanyProductUserType,
  Link,
  Recipient
} from '@setvi/shared/interfaces';
import {
  cleanHtml,
  htmlTags,
  parseHtmlFEtoBC,
  resourceWithChildResources
} from '@setvi/shared/utils';

import {
  useEditorLinksHook,
  useEditorProducts
} from '@setvi/shared/components/seditor/hooks';
import { useDialog } from '@setvi/shared/providers';
import {
  EditorProduct,
  GetLinkActionProps
} from '@setvi/shared/components/seditor/interfaces';
import { DRAWER_WIDTH } from '@setvi/shared/components/sdrawer-content/components/base';
import { NylasAPIVersion } from '@setvi/shared/services/react-query/query/user/types';
import { NylasStatus } from 'Services/Mutations/User/Types';

import { useNylas } from 'Hooks/useNylas';
import NylasDialog from 'Components/Campaigns/NylasDialog';
import {
  getNylasAccountStatus,
  sendEMailMutation,
  userSignatureQuery
} from 'Services';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { addFreeSpaceToEmailContent } from 'Utils/addFreeSpaceToEmailContent';
import { useUploadResourcePanel } from 'providersV2/upload-resource-panel/context';

import { CampaignFormLimit } from 'Enums/CampaignFormLimit';
import { DiscardEmailConfirmDialog } from 'Components/Shared';
import { useEmailServer } from 'pages/settings/subpages/integrations/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EditorActions } from '@setvi/shared/components/seditor/enums';
import { openComposeProps } from '../context';
import { EmailDimensions } from '..';

export interface ComposeEmailValues {
  sendTo: Recipient[];
  subject: string;
  body: string;
  textBody: string;
}

const initialValues: ComposeEmailValues = {
  sendTo: [] as Recipient[],
  subject: '',
  body: '',
  textBody: ''
};

export const useCompose = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { setOffset } = useUploadResourcePanel();
  const { openDialog, closeDialog } = useDialog();

  const [loading, setLoading] = useState(false);
  const { mutateAsync: sendMail, isLoading } = useMutation(sendEMailMutation());
  const [values, setValues] = useState(initialValues);
  const [openComposeEmail, setOpenComposeEmail] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentDrawerContent, setCurrentDrawerContent] =
    useState<DrawerContent | null>(null);
  const [openLinkId, setOpenLinkId] = useState('');
  const [editorNode, setEditorNode] = useState(null);
  const [importedLinks, setImportedLinks] = useState<Link[]>([]);
  const [importedProducts, setImportedProducts] = useState<EditorProduct[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isInit, setIsInit] = useState(true);

  const { data: signature, isFetching: isSignatureFetching } =
    useQuery(userSignatureQuery());
  const { nylasToken, companyData, user } = useAppContext();
  const { connect } = useEmailServer();
  const {
    getStatus: getNylasStatus,
    statusLoading,
    connectV3
  } = useNylas(nylasToken);
  const flags = useFlags();
  const userType = useMemo(
    () =>
      companyData?.CompanySettingsList?.find(
        setting => setting.CompanySettingsTypeID === 38
      )?.Value as CompanyProductUserType,
    [companyData]
  );
  const { mutateAsync: getNylasStatusV3, isLoading: nylasStatusLoading } =
    useMutation(getNylasAccountStatus());

  const getLinkAction = useCallback(({ action, id }: GetLinkActionProps) => {
    setIsDrawerOpen(true);
    setCurrentDrawerContent(DrawerContent.reviewLinks);

    setOpenLinkId(action === EditorActions.EDIT ? id : null);
  }, []);

  const { handleLinks, handleSnippet, setLinks, links, initLinkOptions } =
    useEditorLinksHook({
      editorRef: editorNode,
      importedLinks: isInit ? [] : importedLinks,
      getLinkAction
    });

  const { initProductsOptions, handleProducts } = useEditorProducts({
    products: importedProducts,
    editorRef: editorNode,
    manufacturer: userType === CompanyProductUserType.Manufacturer
  });

  const removeLink = (deleteLink: Link) => {
    handleLinks(EditorActions.REMOVE, { link: deleteLink });
  };

  const clearValues = () => setValues(initialValues);

  const closeComposeEmail = () => {
    setLinks([]);
    clearValues();
    setImportedLinks([]);
    setImportedProducts([]);
    setEditorNode(null);
    setOpenComposeEmail(false);
    setCurrentDrawerContent(null);
    setIsInit(true);
  };

  const open = useCallback(
    (
      {
        linksData,
        drawerContent,
        emailBody,
        products,
        subject,
        recipients
      }: openComposeProps,
      disableOpening?: boolean
    ) => {
      if (!disableOpening) {
        setOpenComposeEmail(true);
        setIsExpanded(true);
      }

      if (linksData) {
        setImportedLinks(Array.isArray(linksData) ? linksData : [linksData]);
        setValues(prev => ({
          ...prev,
          sendTo: recipients || [],
          subject: subject || ''
        }));
      }
      if (drawerContent) setCurrentDrawerContent(drawerContent);
      if (emailBody) setValues(prev => ({ ...prev, body: emailBody }));
      if (products) {
        if (!editorNode) return setImportedProducts(products);
        products.forEach(product =>
          handleProducts(EditorActions.INSERT, { product, editorNode })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, editorNode]
  );

  const close = () => {
    setOpenComposeEmail(false);
    setCurrentDrawerContent(null);
  };

  const insertLink = useCallback(
    (newLink: Link, oldLink?: Link) => {
      if (!oldLink) handleLinks(EditorActions.INSERT, { link: newLink });
      else handleLinks(EditorActions.UPDATE, { link: newLink, oldLink });
    },
    [handleLinks]
  );

  const toggleDrawer = (content?: DrawerContent | null) => {
    setCurrentDrawerContent(content || null);
    setIsDrawerOpen(!!content);
  };

  // API FUNCTIONS

  const addContactToRecipients = (contact: TContact) =>
    setValues(preValues => ({
      ...preValues,
      sendTo: [
        ...preValues.sendTo,
        {
          ...(contact?.Id && { Id: contact.Id }),
          Email: contact.Email,
          CRMWhatId: contact.CRMWhatId || '',
          CRMWhoId: contact.CRMWhoId || ''
        }
      ]
    }));

  const removeContactFromRecipients = (contact: TContact) =>
    setValues(preValues => ({
      ...preValues,
      sendTo: preValues.sendTo.filter(
        (recipient: any) => recipient.Email !== contact.Email
      )
    }));

  const modifyRecipientList = (contact: TContact) => {
    const isContactInList: boolean = values.sendTo.some(
      (recipient: any) => recipient.Email === contact.Email
    );

    if (isContactInList) return removeContactFromRecipients(contact);

    return addContactToRecipients(contact);
  };

  const onSubmit = async () => {
    if (cleanHtml(values.body)?.length > CampaignFormLimit.Body) return;
    const isV3 = user.NylasAPIVersion === NylasAPIVersion.v3;

    if (flags?.integrations) {
      // Enables reconnecct only if user has been connected to nylas in v2 or v3
      const enableReconnectDialog = Boolean(user?.NylasGrantID || nylasToken);
      let isNylasV2Invalid = false;
      let isNylasV3Invalid = isV3 && !user?.NylasGrantID;
      if (isV3 && !isNylasV3Invalid) {
        let nylasResponseV3;
        try {
          nylasResponseV3 = await getNylasStatusV3();
        } catch (e) {
          nylasResponseV3 = null;
        }

        if (nylasResponseV3) {
          isNylasV3Invalid =
            nylasResponseV3.Data?.Status === NylasStatus.Invalid;
        } else {
          isNylasV3Invalid = true;
        }
      } else if (!isV3 && nylasToken) {
        const nylasResponse = await getNylasStatus();
        isNylasV2Invalid =
          nylasResponse.message ||
          nylasResponse.sync_state === NylasStatus.Invalid;
      }

      if (enableReconnectDialog && (isNylasV2Invalid || isNylasV3Invalid))
        return openDialog(
          <NylasDialog
            customReconnect={() =>
              openDialog(
                <DiscardEmailConfirmDialog
                  confirmText="Discard & Reconnect"
                  onConfirm={() => {
                    closeDialog();
                    closeComposeEmail();
                    isDrawerOpen && toggleDrawer();
                    isV3
                      ? connectV3(
                          companyData?.EmailSyncCredentials?.ClientID_V3
                        )
                      : connect();
                  }}
                />
              )
            }
          />
        );
    }

    const { body, textBody, sendTo, subject } = values;

    const parsedBody = {
      body: {
        Subject: subject,
        HtmlBody: parseHtmlFEtoBC(body),
        Recipients: sendTo,
        PlainBody: textBody,
        Links: links.map(link => ({
          Objects: link.ParentID
            ? [
                {
                  ObjectId: link.ParentID,
                  ObjectType: LinkObjectType.Presentation
                }
              ]
            : link.Item.Items.map(item =>
                resourceWithChildResources.includes(item.ResourceTypeID) &&
                item.Children?.length > 0
                  ? item.Children.map(c => ({
                      ObjectId: c.ID,
                      ObjectType: c.ObjectType
                    }))
                  : {
                      ObjectId: item.ID,
                      ObjectType: item.ObjectType
                    }
              ).flat(),
          LinkName: link.Name,
          Placeholder: link.Placeholder
        }))
      }
    };

    const { Data } = await sendMail(parsedBody);

    setLoading(false);

    if (Data) closeComposeEmail();

    enqueueSnackbar(
      Data
        ? 'Email is added to a Queue and It will be scheduled to send soon'
        : 'Something went wrong, please try again later',
      {
        variant: Data ? 'success' : 'error'
      }
    );
  };

  // CHIP FUNCTIONS

  const addListOfContactsToRecipients = (contacts: TContact[]) => {
    const filteredRecipients: Recipient[] = [];

    contacts.forEach(contact => {
      const isDuplicate: boolean = values.sendTo.some(
        c => contact.Email.toLowerCase() === c.Email.toLowerCase()
      );

      if (!isDuplicate) filteredRecipients.push({ Email: contact.Email });
    });

    setValues({
      ...values,
      sendTo: [...values.sendTo, ...filteredRecipients]
    });
  };

  const removeListOfContactsFromRecipients = (contacts: TContact[]) => {
    const filteredRecipients: Recipient[] = values.sendTo.filter(
      recipient =>
        !contacts.some(
          contact =>
            contact.Email.toLowerCase() === recipient.Email.toLowerCase()
        )
    );

    setValues({
      ...values,
      sendTo: filteredRecipients
    });
  };

  const modifyRecipientListWithListOfContacts = (
    contacts: TContact[],
    add: boolean
  ) => {
    if (add) return addListOfContactsToRecipients(contacts);
    removeListOfContactsFromRecipients(contacts);
  };

  const handleSnipset = (value: string) => {
    handleSnippet(value);
  };

  useEffect(() => {
    if (!editorNode) return;

    if (isSignatureFetching) editorNode?.setProgressState(isSignatureFetching);
    else {
      const hasSignature = cleanHtml(signature)?.length > 0;

      setValues((prev: any) => ({
        ...prev,
        body: `${prev.body}${hasSignature ? addFreeSpaceToEmailContent(signature) : ''}`
      }));
      setIsInit(false);
    }
  }, [editorNode, isSignatureFetching, setValues, signature]);

  useEffect(() => {
    if (openComposeEmail) {
      let offset = 40;
      if (!isExpanded) offset += EmailDimensions.MAX_WIDTH_MINIMIZED;
      else offset += EmailDimensions.MAX_WIDTH_EXPANDED + 20;

      if (isDrawerOpen) offset += DRAWER_WIDTH;

      setOffset(offset);
    } else setOffset(20);
  }, [isDrawerOpen, openComposeEmail, isExpanded, setOffset]);

  const onCloseCompose = () => {
    if (
      values.sendTo.length === 0 &&
      values.subject.trim().length === 0 &&
      values.body.replace(htmlTags, '').trim().length === 0
    ) {
      if (isDrawerOpen) toggleDrawer();
      return closeComposeEmail();
    }

    openDialog(
      <DiscardEmailConfirmDialog
        onConfirm={() => {
          closeDialog();
          closeComposeEmail();
          isDrawerOpen && toggleDrawer();
        }}
      />
    );
  };

  const minmizedElements = [
    {
      key: '1',
      icon: <ExpandLessRoundedIcon htmlColor="white" />,
      onClick: () => setIsExpanded(true)
    }
  ];

  const headerElements = [
    {
      key: '1',
      icon: <RemoveRoundedIcon />,
      onClick: () => {
        setIsExpanded(false);
        setIsDrawerOpen(false);
      }
    },
    {
      key: '2',
      icon: <ClearIcon />,
      onClick: () => onCloseCompose()
    }
  ];

  const title = useMemo(
    () => values?.subject || 'Compose Email',
    [values?.subject]
  );

  const rightMargin = useMemo(() => {
    if (isDrawerOpen) return DRAWER_WIDTH + 20;

    return isExpanded ? 40 : 20;
  }, [isExpanded, isDrawerOpen]);

  const link = useMemo(
    () =>
      openLinkId
        ? links.find(addedLink => addedLink?.Placeholder === openLinkId)
        : null,
    [openLinkId, links]
  );

  return {
    link,
    links,
    title,
    values,
    editorNode,
    isLoading: isLoading || loading || statusLoading || nylasStatusLoading,
    isExpanded,
    isDrawerOpen,
    rightMargin,
    initLinkOptions,
    initProductsOptions,
    openComposeEmail,
    minmizedElements,
    headerElements,
    currentDrawerContent,

    open,
    close,
    onSubmit,
    setValues,
    insertLink,
    removeLink,
    toggleDrawer,
    setOpenLinkId,
    setEditorNode,
    handleSnipset,
    setIsExpanded,
    closeComposeEmail,
    modifyRecipientList,
    setCurrentDrawerContent,
    modifyRecipientListWithListOfContacts
  };
};
